/**
 * AJAXCommunications_JS Definitions.
 */

// #region DEFINITIONS

/*************************** DATATYPE **********************************/

/**
 * Callback on success.
 * @callback SUCCESS_AJAXCALLBACK
 * @param {AJAX_RESPONSE} response  ajax response.
 */

/**
 * Callback on error.
 * @callback ERROR_AJAXCALLBACK
 * @param {AJAX_ERROR} response  ajax response (connection was not establish).
 */

/**
 * AJAXCommunications_JS options.
 * @typedef {Object} AJAXCOMMUNICATION_OPTIONS
 * @property {SUCCESS_AJAXCALLBACK} FnSuccess Callback on successful communication.
 * @property {ERROR_AJAXCALLBACK} FnError Callback on communication error.
 * @property {Function} FnBeforeSend Parameterless callback before fetch communication.
 * @property {Function} FnComplete Parameterless Callback on complete communication.
 * @property {Object} headers HTTP headers.
 */

/**
 * Send data asynchronously via Fetch API.
 * @typedef {Object} AJAX_OPTIONS
 * @property {String} url Target url.
 * @property {Object|FormData} data Object or formData to send. If data is type of object then content-type is equal to application/json. 
 * @property {String} method HTTP method.
 * @property {String} [fetchMode=JFM_CONST.FETCHMODES.SAMEORIGIN] fetch mode {@link JFM_CONST}
 * @property {String} [fetchResponseMethod=JFM_CONST.FETCH_REPONSES.JSON] {@link JFM_CONST}
 * @property {String} [redirectFetchResponseMethod=JFM_CONST.FETCH_REPONSES.TEXT] {@link JFM_CONST}
 * @property {Object} headers HTTP headers.
 * @property {SUCCESS_AJAXCALLBACK} fnSuccess Callback on successful communication.
 * @property {ERROR_AJAXCALLBACK} fnError Callback on communication error.
 * @property {Function} fnBeforeSend Parameterless callback before fetch communication.
 * @property {Function} fnComplete Parameterless Callback on complete communication.
 */

/**
 * Error response.
 * @typedef {Object} AJAX_ERROR
 * @property {String} message error message (javascript exception).
 * @property {String} stack stack trace (javascript exception).
 * @property {String} status status code (server exception).
 * @property {String} statusText status text (server exception).
 */

/**
 * Application message response. (Server response).
 * @typedef {Object} APP_MESSAGE_RESPONSE
 * @property {Boolean} IsSuccessful Flag to indicate if the operation was successful.
 * @property {Array.<String>} Reason Collection of messages from the server.
 * @property {Object} Response Response Object.
 * @property {Boolean} IsException Flag to indicate that a server-exception was raised.
 */

/**
 * Ajax response.
 * @typedef {Object} AJAX_RESPONSE
 * @property {APP_MESSAGE_RESPONSE} data Server response.
 * @property {String} isRedirected HTTP status code 30X (redirection response).
 * @property {String} contentType Content type header value. 
 * @property {Number} contentLength Content length header value.
 * @property {String} contentDisposition Content disposition header value (filename value).
 */

/*************************** EVENTS **********************************/

/**
* "fileprogress" event. Retrieve percent progress. Uses {@link Observable_JS} to subscribe and listen events.
* @event AJAXCommunications_JS#fileprogress
* @type {Object} 
* @property {Number} percentComplete - Progress 0-100%.
* @example 
*   const observable = new Observable_JS();
* 
*    function showProgress({percentComplete}) {
*    fileprogress.textContent = percentComplete;
*  }
*   observable.subscribe("fileprogress", showProgress);
*   
*   AJAXCommunications_JS.uploadFile(file, url, observable);
* @example 
*   const observable = new Observable_JS();
* 
*    function showProgress({percentComplete}) {
*    fileprogress.textContent = percentComplete;
*  }
*   observable.subscribe("fileprogress", showProgress);
*   
*   AJAXCommunications_JS.downloadFile(url,null,observable);
*/

/**
* "fileload" event. Returns status and server response once the file is processed by the server. Uses {@link Observable_JS} to subscribe and listen events.
* @event AJAXCommunications_JS#fileload
* @type {Object} 
* @property {String} status - XMLHttpRequest status.
* @property {String} statusText - XMLHttpRequest status text.
* @property {APP_MESSAGE_RESPONSE} response - Server response.
* @example 
*   const observable = new Observable_JS();
* 
*   function showResult({status, statusText, response}) {
*       console.log('status',status,statusText);
*       console.table(response);
*   }
*   observable.subscribe("fileload", showResult);
*   
*   AJAXCommunications_JS.uploadFile(file, url, observable);
*/

/**
* "fileerror" event. Returns status and server response when an error communication raised. Uses {@link Observable_JS} to subscribe and listen events.
* @event AJAXCommunications_JS#fileerror
* @type {Object} 
* @property {String} status - XMLHttpRequest status.
* @property {String} statusText - XMLHttpRequest status text.
* @property {APP_MESSAGE_RESPONSE} response - Server response.
* @example 
*   const observable = new Observable_JS();
* 
*   function showResult({status, statusText, response}) {
*       console.log('status',status,statusText);
*       console.table(response);
*   }
*   observable.subscribe("fileerror", showResult);
*   
*   AJAXCommunications_JS.uploadFile(file, url, observable);
*/
// #endregion

/**
 * Establish ajax communications with the server
 * @tutorial AJAXCommunications_JS
 */
class AJAXCommunications_JS {
    /**
     * @param {AJAXCOMMUNICATION_OPTIONS} options ajax options.
     */
    constructor(options) {
        /**
         * @type {AJAXCOMMUNICATION_OPTIONS}
         */
        this.Options = {
            method: "get",
            url: null,
            data: null,
            mode: JFM_CONST.FETCHMODES.SAMEORIGIN,
            contentType: null,
            FnSuccess: null,
            FnError: null,
            FnBeforeSend: null,
            FnComplete: null,
            headers: {},
        };

        Object.assign(this.Options, options);
    }
    /**
     * Send data asynchronously via Fetch API.
     * Note: for files, target server must respond with header : "Access-Control-Expose-Headers:Content-Disposition".
     * @param {String} url target url
     * @param {Object|FormData} data Object or formData to send. If data is object then Content-Type is equal to application/json.
     * @param {String} method http method
     * @param {String} [fetchMode=JFM_CONST.FETCHMODES.SAMEORIGIN] fetch mode {@link JFM_CONST}
     * @param {String} [fetchResponseMethod=JFM_CONST.FETCH_REPONSES.JSON] response method {@link JFM_CONST}
     * @param {String} [redirectFetchResponseMethod=JFM_CONST.FETCH_REPONSES.TEXT] redirect response method {@link JFM_CONST}
     * @param {Object} headers http headers
     * @returns {Promise<AJAX_RESPONSE>|Promise<AJAX_ERROR>} fetch response
     * @tutorial AsyncSend
     */
    async asyncSend(url, data, method = "Get", fetchMode = JFM_CONST.FETCHMODES.SAMEORIGIN, fetchResponseMethod = JFM_CONST.FETCH_REPONSES.JSON,
        redirectFetchResponseMethod = JFM_CONST.FETCH_REPONSES.TEXT, headers = {}) {
        let isFinished = false,
            isRedirected = false,
            connectionWasSuccessful = false,
            attempt = 0,
            contentType = "",
            contentLength = 0,
            contentDisposition = "",
            /**
             * @type {AJAX_RESPONSE}
             */
            objAjaxResponse = null,
            /**
             * @type {AJAX_ERROR}
             */
            objAjaxError = null,
            responseMethod = null,
            body = null;

        const _headers = {
            "X-Requested-With": "XMLHttpRequest",
            ...this.Options.headers,
            ...headers,
        };

        if (data instanceof FormData) {
            delete _headers['Content-Type'];
            body = data;
        } else {
            _headers['Content-Type'] = JFM_CONST.CONTENTTYPE.JSON;
            body = !!data ? JSON.stringify(data) : {};
        }

        const _fetchOptions = {
            method: method,
            body: body,
            headers: _headers,
            redirect: "follow",
        };

        if (fetchMode == JFM_CONST.FETCHMODES.CORS) {
            _fetchOptions.credentials = "include";
        }

        // fecth data
        do {
            if (typeof this.Options.FnBeforeSend === "function") {
                this.Options.FnBeforeSend();
            }

            await fetch(url, _fetchOptions)
                .then((res) => {
                    if (res.ok) {
                        contentLength = res.headers.get("Content-Length");
                        contentDisposition = res.headers.get("Content-Disposition");
                        contentType = res.headers.get("Content-Type");
                        if (res.redirected) {
                            responseMethod = redirectFetchResponseMethod;
                            isRedirected = true;
                        } else {
                            responseMethod = fetchResponseMethod;
                        }
                        switch (responseMethod) {
                            case JFM_CONST.FETCH_REPONSES.ARRAY_BUFFER:
                                return res.arrayBuffer();
                            case JFM_CONST.FETCH_REPONSES.BLOB:
                                return res.blob();
                            case JFM_CONST.FETCH_REPONSES.FORMDATA:
                                return res.formData();
                            case JFM_CONST.FETCH_REPONSES.JSON:
                                return res.json();
                            case JFM_CONST.FETCH_REPONSES.TEXT:
                                return res.text();
                        }
                    } else {
                        return Promise.reject(res);
                    }
                })
                .then((res) => {
                    isFinished = true;
                    connectionWasSuccessful = true;
                    objAjaxResponse = {
                        data: res,
                        isRedirected: isRedirected,
                        contentType: contentType,
                        contentLength : contentLength,
                        contentDisposition : contentDisposition
                    };
                    if (typeof this.Options.FnSuccess === "function") {
                        this.Options.FnSuccess(objAjaxResponse);
                    }
                })
                .catch((res) => {
                    attempt++;
                    if (attempt == JFM_CONST.ATTEMPTS.AJAX) {
                        isFinished = true;
                        connectionWasSuccessful = false;
                        res = !res ? {} : res;
                        objAjaxError = {
                            message: res?.message,
                            stack: res?.stack,
                            status: res?.status,
                            statusText: res?.statusText,
                        };
                        if (typeof this.Options.FnError === "function") {
                            this.Options.FnError(objAjaxError);
                        }
                    }
                })
                .finally(() => {
                    if (typeof this.Options.FnComplete === "function") {
                        this.Options.FnComplete();
                    }
                });
        } while (!isFinished);

        return connectionWasSuccessful
            ? Promise.resolve(objAjaxResponse)
            : Promise.reject(objAjaxError);
    }
    /**
     * Send data asynchronously via Fetch API.
     * Note: (only files) Target server must respond with header : "Access-Control-Expose-Headers:Content-Disposition".
     * @param {AJAX_OPTIONS} options communication parameters
     * @returns {Promise<AJAX_RESPONSE>|Promise<AJAX_ERROR>} fetch response
     * @tutorial staticAsyncSend
     */
    static async asyncSend(options) {
        let isFinished = false,
            isRedirected = false,
            connectionWasSuccessful = false,
            attempt = 0,
            contentType = "",
            contentLength = 0,
            contentDisposition = "",
            /**
             * @type {AJAX_RESPONSE}
             */
            objAjaxResponse = null,
            /**
             * @type {AJAX_ERROR}
             */
            objAjaxError = null,
            responseMethod = null,
            body = null;
        /**
         * @type {AJAX_OPTIONS}
         */
        const _options = {
            fetchMode: JFM_CONST.FETCHMODES.SAMEORIGIN,
            fetchResponseMethod: JFM_CONST.FETCH_REPONSES.JSON,
            redirectFetchResponseMethod: JFM_CONST.FETCH_REPONSES.TEXT,
            data: {},
            method: 'Get',
            url: '',
            headers: {},
            fnBeforeSend: null,
            fnComplete: null,
            fnError: null,
            fnSuccess: null,
            ...options
        },
            _headers = {
                "X-Requested-With": "XMLHttpRequest",
                ..._options.headers
            };
        if (_options.data instanceof FormData) {
            delete _headers["Content-Type"];
            body = _options.data;
        } else {
            _headers["Content-Type"] = JFM_CONST.CONTENTTYPE.JSON;
            if (_options.method.toLowerCase() !== 'get') {
                body = !!_options.data ? JSON.stringify(_options.data) : {};
            }
        }

        const _fetchOptions = {
            method: _options.method,
            body: body,
            headers: _headers,
            redirect: "follow",
        };

        if (_options.fetchMode == JFM_CONST.FETCHMODES.CORS) {
            _fetchOptions.credentials = "include";
        }

        // fecth data
        do {
            if (typeof _options.fnBeforeSend === "function") {
                _options.fnBeforeSend();
            }

            await fetch(_options.url, _fetchOptions)
                .then((res) => {
                    if (res.ok) {
                        contentLength = res.headers.get("Content-Length");
                        contentDisposition = res.headers.get("Content-Disposition");
                        contentType = res.headers.get("Content-Type");
                        if (res.redirected) {
                            responseMethod = _options.redirectFetchResponseMethod;
                            isRedirected = true;
                        } else {
                            responseMethod = _options.fetchResponseMethod;
                        }
                        switch (responseMethod) {
                            case JFM_CONST.FETCH_REPONSES.ARRAY_BUFFER:
                                return res.arrayBuffer();
                            case JFM_CONST.FETCH_REPONSES.BLOB:
                                return res.blob();
                            case JFM_CONST.FETCH_REPONSES.FORMDATA:
                                return res.formData();
                            case JFM_CONST.FETCH_REPONSES.JSON:
                                return res.json();
                            case JFM_CONST.FETCH_REPONSES.TEXT:
                                return res.text();
                        }
                    } else {
                        return Promise.reject(res);
                    }
                })
                .then((res) => {
                    isFinished = true;
                    connectionWasSuccessful = true;
                    objAjaxResponse = {
                        data: res,
                        isRedirected: isRedirected,
                        contentType: contentType,
                        contentDisposition : contentDisposition,
                        contentLength : contentLength
                    };
                    if (typeof _options.fnSuccess === "function") {
                        _options.fnSuccess(objAjaxResponse);
                    }
                })
                .catch((res) => {
                    attempt++;
                    if (attempt == JFM_CONST.ATTEMPTS.AJAX) {
                        isFinished = true;
                        connectionWasSuccessful = false;
                        res = !res ? {} : res;
                        objAjaxError = {
                            message: res?.message,
                            stack: res?.stack,
                            status: res?.status,
                            statusText: res?.statusText,
                        };
                        if (typeof _options.fnError === "function") {
                            _options.fnError(objAjaxError);
                        }
                    }
                })
                .finally(() => {
                    if (typeof _options.fnComplete === "function") {
                        _options.fnComplete();
                    }
                });
        } while (!isFinished);

        return connectionWasSuccessful
            ? Promise.resolve(objAjaxResponse)
            : Promise.reject(objAjaxError);
    }
    /**
     * Send Data to a target url. The response is treated with Callbacks.
     * @deprecated Not use this method anymore.
     */
    async AsyncFetch() {
        let objThis = this,
            isFinished = false,
            isRedirected = false,
            attempt = 0,
            contentType = "",
            fetchOptions = {
                method: this.Options.method,
                body: this.Options.data,
                redirect: "follow",
            };

        // Add the headers
        if (this.Options.contentType) {
            fetchOptions.headers = {
                "Content-Type": this.Options.contentType,
            };
        }

        fetchOptions.headers = {
            ...fetchOptions.headers,
            "X-Requested-With": "XMLHttpRequest",
        };

        if (objThis.Options.mode == JFM_CONST.FETCHMODES.CORS) {
            fetchOptions.credentials = "include";
        }

        if (this.Options.FnBeforeSend) {
            this.Options.FnBeforeSend();
        }

        do {
            await fetch(this.Options.url, fetchOptions)
                .then((res) => {
                    if (res.ok) {
                        contentType = res.headers.get("Content-Type");
                        if (res.redirected) {
                            isRedirected = true;
                            return res.text();
                        } else if (contentType.indexOf(JFM_CONST.CONTENTTYPE.JSON) !== -1) {
                            return res.json();
                        } else if (contentType.indexOf(JFM_CONST.CONTENTTYPE.TEXT) !== -1) {
                            return res.text();
                        } else if (contentType.indexOf(JFM_CONST.CONTENTTYPE.ZIP) !== -1) {
                            return res.blob();
                        } else if (
                            contentType.indexOf(JFM_CONST.CONTENTTYPE.XEXCEL) !== -1
                        ) {
                            return res.blob();
                        } else {
                            return res.text();
                        }
                    } else {
                        return Promise.reject(res);
                    }
                })
                .then((dataR) => {
                    isFinished = true;
                    if (this.Options.FnSuccess) {
                        this.Options.FnSuccess(dataR, isRedirected, contentType);
                    }
                })
                .catch((res) => {
                    attempt++;
                    if (attempt == JFM_CONST.ATTEMPTS.AJAX) {
                        isFinished = true;
                        res = !res ? {} : res;
                        if (this.Options.FnError) {
                            this.Options.FnError(
                                res.statusText,
                                res.status,
                                res.message,
                                res.stack
                            );
                        }
                    }
                })
                .finally(() => {
                    if (isFinished) {
                        if (this.Options.FnComplete) {
                            this.Options.FnComplete();
                        }
                    }
                });
        } while (!isFinished);
    }
    /**
     * @deprecated since October 2023.
     * @description Send Data to a server in JSON format.
     * @param {String} pUrl Target url.
     * @param {Object} pData Unserialized object to send.
     * @param {String} pMethod Http method.
     * @param {string} [pFetchMode=JFM_CONST.FETCHMODES.SAMEORIGIN] Fetch mode (cors or same origin).  {@link JFM_CONST.FETCHMODES}
     * @param {string} [pFetchResponseMethod=JFM_CONST.FETCH_REPONSES.JSON] Response method. {@link JFM_CONST.FETCH_REPONSES}
     * @param {string} [pRedirectFetchResponseMethod=JFM_CONST.FETCH_REPONSES.TEXT] Redirect response method. {@link JFM_CONST.FETCH_REPONSES}
     * @returns {Promise} Response from fetch API.
     */
    static async AsyncFetchJSON(
        pUrl,
        pData,
        pMethod,
        pFetchMode = JFM_CONST.FETCHMODES.SAMEORIGIN,
        pFetchResponseMethod = JFM_CONST.FETCH_REPONSES.JSON,
        pRedirectFetchResponseMethod = JFM_CONST.FETCH_REPONSES.TEXT
    ) {
        let isFinished = false,
            isRedirected = false,
            connectionWasSuccessful = false,
            attempt = 0,
            contentType = "",
            /**
             * @type {AJAX_RESPONSE}
             */
            objAjaxResponse = null,
            /**
             * @type {AJAX_ERROR}
             */
            objAjaxError = null,
            responseMethod = null,
            fetchHeaders = {
                "X-Requested-With": "XMLHttpRequest",
                "Content-Type": JFM_CONST.CONTENTTYPE.JSON,
            };

        const fetchOptions = {
            method: pMethod,
            body: !!pData ? JSON.stringify(pData) : {},
            headers: fetchHeaders,
            redirect: "follow",
        };

        if (pFetchMode == JFM_CONST.FETCHMODES.CORS) {
            fetchOptions.credentials = "include";
        }

        // Request to the server
        do {
            await fetch(pUrl, fetchOptions)
                .then((res) => {
                    if (res.ok) {
                        contentType = res.headers.get("Content-Type");
                        if (res.redirected) {
                            responseMethod = pRedirectFetchResponseMethod;
                            isRedirected = true;
                        } else {
                            responseMethod = pFetchResponseMethod;
                        }
                        switch (responseMethod) {
                            case JFM_CONST.FETCH_REPONSES.ARRAY_BUFFER:
                                return res.arrayBuffer();
                            case JFM_CONST.FETCH_REPONSES.BLOB:
                                return res.blob();
                            case JFM_CONST.FETCH_REPONSES.FORMDATA:
                                return res.formData();
                            case JFM_CONST.FETCH_REPONSES.JSON:
                                return res.json();
                            case JFM_CONST.FETCH_REPONSES.TEXT:
                                return res.text();
                        }
                    } else {
                        return Promise.reject(res);
                    }
                })
                .then((res) => {
                    isFinished = true;
                    connectionWasSuccessful = true;
                    objAjaxResponse = {
                        data: res,
                        isRedirected: isRedirected,
                        contentType: contentType,
                    };
                })
                .catch((res) => {
                    attempt++;
                    if (attempt == JFM_CONST.ATTEMPTS.AJAX) {
                        isFinished = true;
                        connectionWasSuccessful = false;
                        res = !res ? {} : res;
                        objAjaxError = {
                            message: res?.message,
                            stack: res?.stack,
                            status: res?.status,
                            statusText: res?.statusText,
                        };
                    }
                });
        } while (!isFinished);

        return connectionWasSuccessful
            ? Promise.resolve(objAjaxResponse)
            : Promise.reject(objAjaxError);
    }
    /**
     * Send Json data via POST method.
     * @param {String} pUrl Target url.
     * @param {Object} pData Object to Send.
     * @param {String} [pFetchMode=JFM_CONST.FETCHMODES.SAMEORIGIN] Fetch mode. {@link JFM_CONST}.
     * @returns {Promise<AJAX_RESPONSE>|Promise<AJAX_ERROR>} fetch response
     * @tutorial staticPostGetJson
     */
    static async AsyncPostJSON(pUrl, pData, pFetchMode = JFM_CONST.FETCHMODES.SAMEORIGIN) {
        return AJAXCommunications_JS.asyncSend({ data: pData, url: pUrl, method: 'Post', fetchMode: pFetchMode });
    }
    /**
     * Request Json data via GET method.
     * @param {String} pUrl Target url.
     * @param {Object} pData Append data to target url. Note: Plain object, not inner array neither inner objects.
     * @param {String} [pFetchMode=JFM_CONST.FETCHMODES.SAMEORIGIN] Fetch mode. Use constant values {@link JFM_CONST}.
     * @returns {Promise<AJAX_RESPONSE>|Promise<AJAX_ERROR>} fetch response
     * @tutorial staticPostGetJson
     */
    static async AsyncGetJSON(pUrl, pData, pFetchMode = JFM_CONST.FETCHMODES.SAMEORIGIN) {
        let  querystring = '';
        if (pData && typeof pData === 'object') {
            for (const key in pData) {
                if (Object.hasOwnProperty.call(pData, key)) {
                    if (typeof pData[key] === 'boolean' || typeof pData[key] === 'number' || typeof pData[key] === 'string') {
                        if (querystring) {
                            querystring += '&';
                        }
                        else{
                            querystring = '?'
                        }
                        querystring+= `${key}=${pData[key]}`
                    }
                }
            }
        }
        !!querystring && (pUrl+=querystring);
        return AJAXCommunications_JS.asyncSend({ data: null, url: pUrl, method: 'Get', fetchMode: pFetchMode });
    }
    /**
     * Upload a file using {@link XMLHttpRequest} via Post method.
     * Server response in Json format.
     * @param {Blob} file File to upload.
     * @param {String} url Target server.
     * @param {Observable_JS} observable Subscribe and listen events.
     * @fires AJAXCommunications_JS#fileprogress  
     * @fires AJAXCommunications_JS#fileload
     * @fires AJAXCommunications_JS#fileerror
     * @tutorial staticAjaxUploadFile
     */
    static async uploadFile(file, url, observable){
        //inner function
        function xhrSend(file, url, observable, attemp = 0) {
          const _payload = new FormData(),
            _xhr = new XMLHttpRequest();

          _payload.append("File", file, file.name);

          _xhr.open("POST", url);
          _xhr.setRequestHeader("Accept", "application/json");
          _xhr.responseType = "json";
          //progress event
          _xhr.upload.addEventListener("progress", function (e) {
            const _percentComplete = parseInt(
              Math.ceil((e.loaded / e.total) * 100)
            );
            observable.notify("fileprogress", {percentComplete: _percentComplete});
          });
          //load event
          _xhr.addEventListener("load", function () {
            observable.notify("fileload", {status: _xhr.status, statusText :_xhr.statusText , response : _xhr.response});
          });
          //error event
          _xhr.addEventListener("error", function () {
            //3 attemps to upload file
            if (attemp < JFM_CONST.ATTEMPTS.AJAX) {
              xhrSend(file, url, observable, ++attemp);
              return;
            }
            observable.notify("fileerror", {status: _xhr.status, statusText :_xhr.statusText , response : _xhr.response});
          });
          _xhr.send(_payload);
        }
        xhrSend(file, url , observable);
    }
    /**
     * Download a file via Fetch API. Raise progress event through observable object.
     * Note: (only files) Target server must respond with header : "Access-Control-Expose-Headers:Content-Disposition".
     * @param {String} url Target url.
     * @param {Object|FormData} data Object or form data to send.
     * @param {Observable_JS} observable Subscribe and listen events.
     * @param {String} [headers={}] HTTP headers.
     * @param {String} [method="Get"] HTTP method.
     * @param {String} [fetchMode=JFM_CONST.FETCHMODES.SAMEORIGIN] Fetch mode. {@link JFM_CONST}
     * @returns {Promise<AJAX_RESPONSE>|Promise<AJAX_ERROR>} Fetch response.
     * @fires AJAXCommunications_JS#fileprogress
     * @tutorial staticAjaxDownLoadFile
     */
    static async downloadFile(url, data, observable = new Observable_JS() , headers = {}, method = 'Get',fetchMode = JFM_CONST.FETCHMODES.SAMEORIGIN){
        let isFinished = false,
            isRedirected = false,
            connectionWasSuccessful = false,
            attempt = 0,
            contentLength = 0,
            contentDisposition = '',
            contentType = '',
            /**
             * @type {AJAX_RESPONSE}
             */
            objAjaxResponse = null,
            /**
             * @type {AJAX_ERROR}
             */
            objAjaxError = null,
            body = null;

        const _headers = {
            "X-Requested-With": "XMLHttpRequest",
            ...headers,
        };

        if (data instanceof FormData) {
            delete _headers["Content-Type"];
            body = data;
        } else {
            _headers["Content-Type"] = JFM_CONST.CONTENTTYPE.JSON;
            if (method.toLowerCase() !== 'get') {
                body = !!data ? JSON.stringify(data) : {};
            }
        }

        const _fetchOptions = {
            method: method,
            body: body,
            headers: _headers,
            redirect: "follow",
        };

        if (fetchMode == JFM_CONST.FETCHMODES.CORS) {
            _fetchOptions.credentials = "include";
        }

        /**
         * Build blob response according to download progress
         * @param {Response} res  fetch response
         */
        function downloadResponse(res){
            if (!res.ok) {
                return Promise.reject(res);
            }
            if (res.redirected) {
                isRedirected = true;
                return res.text();
            }
            // get total file length.
            contentLength = res.headers.get('Content-Length');
            contentDisposition = res.headers.get("Content-Disposition");
            contentType = res.headers.get("Content-Type");
            
            let loaded = 0, percentComplete = 0;

            return new Response(
                // Creates new readable stream on the new response object
                new ReadableStream({
                // Controller has methods on that allow the new stream to be constructed
                  start(controller) {
                      // Creates a new reader to read the body of the fetched resources
                    const reader = res.body.getReader();
                    // Fires function below that starts reading
                    read();
                    /**
                     * Read the stream
                     */
                    function read() {
                      reader.read()
                      // Starts reading, when there is progress this function will fire
                      .then((progressEvent) => {
                      // Will finish constructing new stream if reading fetched of resource is complete
                        if (progressEvent.done) {
                          controller.close();
                          return; 
                        }
                        // Increase value of 'loaded' by latest reading of fetched resource
                        loaded += progressEvent.value.byteLength;
                        // compute progress
                        percentComplete = Math.round(loaded/contentLength*100);
                        //notify progress
                        observable.notify("fileprogress", {percentComplete: percentComplete});
                        // Add newly read data to the new readable stream
                        controller.enqueue(progressEvent.value);
                        // Runs function again to continue reading and creating new stream
                        read();
                      })
                    }
                  }
                })
              );
        }

        // fecth data
        do {
            await fetch(url, _fetchOptions)
                .then(downloadResponse)
                .then(response => response.blob()) // Read new readable stream to blob
                .then((res) => {
                    isFinished = true;
                    connectionWasSuccessful = true;
                    objAjaxResponse = {
                        data: res,
                        isRedirected: isRedirected,
                        contentType: contentType,
                        contentLength: contentLength,
                        contentDisposition : contentDisposition
                    };
                })
                .catch((res) => {
                    attempt++;
                    if (attempt == JFM_CONST.ATTEMPTS.AJAX) {
                        isFinished = true;
                        connectionWasSuccessful = false;
                        res = !res ? {} : res;
                        objAjaxError = {
                            message: res?.message,
                            stack: res?.stack,
                            status: res?.status,
                            statusText: res?.statusText,
                        };
                    }
                });
        } while (!isFinished);

        return connectionWasSuccessful
            ? Promise.resolve(objAjaxResponse)
            : Promise.reject(objAjaxError);
    }
}